import './ChartCard.scss';

/**
 * Standard analytics chart card wrapper.
 *
 * Renders a white rounded card with a header row (title + optional controls)
 * and a subtitle below the header.  The `controls` slot accepts any React node
 * — use it for a <select>, <SegmentToggle>, or anything else.
 *
 * The `wide` flag adds the `chart-card--wide` modifier which spans all grid
 * columns when the card lives inside an `.org-reports-grid` (or similar CSS Grid).
 *
 * Usage:
 *   <ChartCard title={t('...')} subtitle={t('...')} controls={<select ...>}>
 *     <MyChart />
 *   </ChartCard>
 */

interface ChartCardProps {
  title: string;
  subtitle?: string;
  /** Optional right-aligned control (dropdown, segment toggle, …). */
  controls?: React.ReactNode;
  children: React.ReactNode;
  /** Adds `chart-card--wide` — spans full grid width. */
  wide?: boolean;
  /** Test automation selector. */
  'data-test'?: string;
}

const ChartCard: React.FC<ChartCardProps> = ({ title, subtitle, controls, children, wide, 'data-test': dataTest }) => (
  <div className={`chart-card${wide ? ' chart-card--wide' : ''}`} data-test={dataTest}>
    <div className='chart-card-header'>
      <div>
        <h3 className='chart-title'>{title}</h3>
        {subtitle && <p className='chart-subtitle'>{subtitle}</p>}
      </div>
      {controls}
    </div>
    {children}
  </div>
);

export default ChartCard;
